/**
 * WebSocket client for real-time simulation updates using Socket.IO
 * Requirements: 4.2, 4.3, 4.4
 */

import type { SimulationConfig } from '@langton/shared';
import type { SerializedState } from '@langton/shared';

type InitCallback = (simId: string, state: SerializedState, config: SimulationConfig) => void;
type SyncCallback = (simId: string, state: SerializedState, config: SimulationConfig) => void;
type ErrorCallback = (simId: string, error: string) => void;

// Socket.IO client type (loaded from CDN)
declare const io: (url?: string) => SocketIOClient;

interface SocketIOClient {
  on(event: string, callback: (...args: any[]) => void): void;
  emit(event: string, data?: any): void;
  connect(): void;
  disconnect(): void;
  connected: boolean;
}

interface InitMessage {
  simulationId: string;
  state: SerializedState;
  config: SimulationConfig;
}

interface SyncMessage {
  simulationId: string;
  state: SerializedState;
  config: SimulationConfig;
}

interface ErrorMessage {
  simulationId: string;
  error: string;
}

/**
 * WebSocket client for connecting to simulation server via Socket.IO
 */
export class WebSocketClient {
  private wsUrl: string;
  private socket: SocketIOClient | null = null;
  private subscriptions: Set<string> = new Set();
  
  private initCallbacks: InitCallback[] = [];
  private syncCallbacks: SyncCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];

  constructor(wsUrl: string) {
    this.wsUrl = wsUrl;
  }

  /**
   * Connect to Socket.IO server
   */
  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    try {
      // Use global io() from Socket.IO CDN
      this.socket = io();
      
      this.socket.on('connect', () => {
        console.log('Socket.IO connected');
        
        // Resubscribe to all simulations
        this.subscriptions.forEach(simId => {
          this.sendSubscribe(simId);
        });
      });

      this.socket.on('simulation:init', (data: InitMessage) => {
        this.initCallbacks.forEach(cb => 
          cb(data.simulationId, data.state, data.config)
        );
      });

      this.socket.on('simulation:sync', (data: SyncMessage) => {
        this.syncCallbacks.forEach(cb => 
          cb(data.simulationId, data.state, data.config)
        );
      });

      this.socket.on('simulation:error', (data: ErrorMessage) => {
        this.errorCallbacks.forEach(cb => 
          cb(data.simulationId, data.error)
        );
      });

      this.socket.on('disconnect', () => {
        console.log('Socket.IO disconnected');
      });

      this.socket.on('connect_error', (error: Error) => {
        console.error('Socket.IO connection error:', error);
      });

    } catch (error) {
      console.error('Failed to connect Socket.IO:', error);
    }
  }

  /**
   * Subscribe to a simulation's updates
   */
  subscribe(simulationId: string): void {
    this.subscriptions.add(simulationId);
    
    if (this.socket?.connected) {
      this.sendSubscribe(simulationId);
    }
  }

  /**
   * Send subscribe message
   */
  private sendSubscribe(simulationId: string): void {
    this.socket?.emit('subscribe', { simulationId });
  }

  /**
   * Unsubscribe from a simulation's updates
   */
  unsubscribe(simulationId: string): void {
    this.subscriptions.delete(simulationId);
    
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { simulationId });
    }
  }

  /**
   * Register callback for init messages
   */
  onInit(callback: InitCallback): void {
    this.initCallbacks.push(callback);
  }

  /**
   * Register callback for sync messages
   */
  onSync(callback: SyncCallback): void {
    this.syncCallbacks.push(callback);
  }

  /**
   * Register callback for error messages
   */
  onError(callback: ErrorCallback): void {
    this.errorCallbacks.push(callback);
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.subscriptions.clear();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}
